import React from 'react';
import type { BirdEyeDropTarget } from '../../bird-eye/boardTypes';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { useBirdEyeDnd } from './BirdEyeDndContext';

export interface BirdEyeLaneProps {
  icon: IconKind;
  title: string;
  accent: string;
  count: string | number;
  sub?: string;
  children: React.ReactNode;
  className?: string;
  dropTarget?: BirdEyeDropTarget;
}

export function BirdEyeLane({
  icon,
  title,
  accent,
  count,
  sub,
  children,
  className,
  dropTarget,
}: BirdEyeLaneProps) {
  const dnd = useBirdEyeDnd();
  const dropHandlers = dropTarget ? dnd.bindDropZone(dropTarget) : undefined;
  const dropActive = dropTarget ? dnd.isDropActive(dropTarget) : false;

  return (
    <div
      className={cn(
        'flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      )}
      style={{ borderTopWidth: 3, borderTopColor: accent }}
    >
      <div className="flex items-center gap-[9px] border-b border-[var(--cream-2)] px-3.5 py-[13px]">
        <span
          className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg"
          style={{
            background: `color-mix(in srgb, ${accent} 14%, transparent)`,
            color: accent,
          }}
        >
          <Icon kind={icon} size={14} stroke={1.9} />
        </span>
        <div className="min-w-0">
          <div className="font-[var(--font-display)] text-sm font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
            {title}
          </div>
          {sub ? <div className="text-[10.5px] text-[var(--ink-3)]">{sub}</div> : null}
        </div>
        <span
          className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 font-[var(--font-mono)] text-[11px] font-bold"
          style={{
            background: `color-mix(in srgb, ${accent} 14%, transparent)`,
            color: accent,
          }}
        >
          {count}
        </span>
      </div>
      <div
        {...dropHandlers}
        className={cn(
          'be-scroll flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto rounded-b-[var(--r-xl)] bg-[var(--cream-0)] p-3 transition-[outline,background] duration-150',
          dropActive && 'be-drop-active',
        )}
      >
        {children}
      </div>
    </div>
  );
}
